import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { Store } from '@ngxs/store';
import { SignInAuth } from '../state-manager/actions/auth.action';
import { AuthSecurity } from '../models/auth/auth-security';
import { User } from '../models/auth/user';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AuthState } from '../state-manager/state/auth.state';
import { map } from 'rxjs/operators';

@Injectable()
export class LoginService {
  user = null;
  userBehaviorSubject = new BehaviorSubject<User>({});
  userObservable = this.userBehaviorSubject.asObservable();
  hash: string;

  constructor(
    private cookieService: CookieService,
    protected store: Store,
    protected http: HttpClient
  ) {
    this.store
      .select(AuthState.HASH)
      .pipe(
        map((hash: string) => {
          if (hash) {
            this.hash = hash;
          }
        })
      )
      .subscribe();
  }

  public async isAuthenticate(): Promise<boolean> {
    if (environment.loginForceDev || this.existsJwt()) {
      const auth = await this.authenticate();
      this.store.dispatch(new SignInAuth(auth));
      this.userBehaviorSubject.next(auth.user);
      return true;
    } else if (this.getParamValueQueryString('hash') || this.hash) {
      const auth: AuthSecurity = { cryptoToken: '', jwt: '', user: {} };
      this.store.dispatch(new SignInAuth(auth));
      this.userBehaviorSubject.next(auth.user);
      return true;
    } else {
      const document = this.cookieService.get('document');
      if (document) {
        window.document.location.href =
          environment.refreshMiddlewareUrl +
          '?' +
          'document' +
          '=' +
          document +
          '&' +
          'returnUrl' +
          '=' +
          window.location.href;
        return false;
      } else {
        window.document.location.href = environment.authMiddlewareUrl;
        return false;
      }
    }
  }

  private getParamValueQueryString(paramName: string) {
    const url = window.location.href;
    let paramValue: string;
    if (url.includes('?')) {
      const httpParams = new HttpParams({ fromString: url.split('?')[1] });
      paramValue = httpParams.get(paramName);
    }
    return paramValue;
  }

  public getCurrentUser() {
    return this.userBehaviorSubject.getValue();
  }

  public async authenticate(): Promise<AuthSecurity> {
    const self = this;
    const auth: AuthSecurity = {};

    const cookie = self.getTokens(
      environment.authCookie,
      environment.cryptoCookie
    );
    auth.jwt = cookie.jwt;
    auth.cryptoToken = cookie.crypto;

    const decodedToken = await self.decodeToken(cookie.jwt);
    const company = decodedToken === '' ? '' : decodedToken.company;

    if (decodedToken.unique_name) {
      const uniqueName =
        Array.isArray(decodedToken.unique_name) &&
        decodedToken.unique_name.length > 0
          ? decodedToken.unique_name[0]
          : decodedToken.unique_name;
      const user: User = {};
      user.document = self.formatCPF(uniqueName);
      user.company = self.formatCNPJ(company);
      user.email = decodedToken.email;
      user.userID = decodedToken.sid;
      user.fullname = decodedToken.given_name;
      auth.user = user;

      this.user = auth.user;
      this.cookieService.delete('document');
      this.cookieService.set('document', user.document);
    }

    return auth;
  }

  private existsJwt() {
    const jwt = this.getTokens(environment.authCookie, environment.cryptoCookie)
      .jwt;
    return jwt !== null && jwt !== '';
  }

  private getTokens(jwtKey: string, cryptoKey: string) {
    const tokens = {
      jwt: '',
      crypto: ''
    };
    if (environment.loginForceDev) {
      tokens.jwt = environment.fakeJWTToken;
      tokens.crypto = environment.fakeCryptoToken;
    } else {
      tokens.jwt = this.cookieService.get(jwtKey);
      tokens.crypto = this.cookieService.get(cryptoKey);
    }
    return tokens;
  }

  private formatCPF(value) {
    return value
      .replace(/(\d{3})/g, '$1.')
      .replace(/((?:\d{3}\.){2}\d{3})\./, '$1-');
  }

  private formatCNPJ(value) {
    if (value === '' || value === null) {
      return '';
    }
    return value.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      '$1.$2.$3/$4-$5'
    );
  }

  private urlBase64Decode(str: string) {
    let output = str.replace(/-/g, '+').replace(/_/g, '/');
    switch (output.length % 4) {
      case 0: {
        break;
      }
      case 2: {
        output += '==';
        break;
      }
      case 3: {
        output += '=';
        break;
      }
    }

    return decodeURIComponent((window as any).escape(window.atob(output)));
  }

  private async decodeToken(token: string) {
    const headers = {
      Authorization: `Bearer ${token}`
    };

    if (token === '' || token === undefined) {
      return '';
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      const url = environment.apiLoginSettings.url + environment.apiLoginSettings.decrypt;
      return await this.http
        .get(url, { headers })
        .toPromise();
    }

    const decoded = this.urlBase64Decode(parts[1]);
    if (!decoded) {
      throw new Error('Cannot decode the token');
    }
    return JSON.parse(decoded);
  }
}
